import React from 'react';
import { Form, Pagination as BSPagination, Button, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * PaginationControls - Componente reutilizable para controles de paginado
 * 
 * Skill: pagination-tinvaz.skill.md
 * 
 * @component
 * @example
 * <PaginationControls
 *   page={2}
 *   totalPages={10}
 *   perPage={20}
 *   total={195}
 *   hasNextPage={true}
 *   hasPrevPage={true}
 *   onPageChange={(page) => changePage(page)}
 *   onPerPageChange={(perPage) => changePerPage(perPage)}
 *   onRefresh={() => refresh()}
 *   pendingUpdates={3}
 * />
 */
const PaginationControls = ({
  // Datos de paginación (REQUERIDOS)
  page,
  totalPages,
  perPage,
  total,
  hasNextPage,
  hasPrevPage,
  
  // Callbacks (REQUERIDOS)
  onPageChange,
  onPerPageChange,
  
  // Opciones UI (OPCIONALES)
  perPageOptions = [10, 20, 50, 100],
  showPerPageSelector = true,
  showResultsCount = true,
  maxVisiblePages = 5,
  size,
  className = '',
  
  // Refresh (OPCIONAL)
  onRefresh,
  loading = false,
  pendingUpdates = 0
}) => {
  /**
   * Calcula el rango de páginas visibles alrededor de la página actual
   */
  const getVisiblePages = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, page - halfVisible);
    let endPage = Math.min(totalPages, page + halfVisible);
    
    // Ajustar si estamos cerca del inicio
    if (page <= halfVisible) {
      endPage = Math.min(totalPages, maxVisiblePages);
    }
    
    // Ajustar si estamos cerca del final
    if (page > totalPages - halfVisible) {
      startPage = Math.max(1, totalPages - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };
  
  const visiblePages = getVisiblePages();
  const showFirstPage = visiblePages[0] > 1;
  const showLastPage = visiblePages[visiblePages.length - 1] < totalPages;
  const showFirstEllipsis = visiblePages[0] > 2;
  const showLastEllipsis = visiblePages[visiblePages.length - 1] < totalPages - 1;
  
  // Calcular rango de resultados
  const startResult = total > 0 ? ((page - 1) * perPage) + 1 : 0;
  const endResult = Math.min(page * perPage, total);
  
  return (
    <div className={`d-flex justify-content-between align-items-center flex-wrap gap-3 ${className}`}>
      {/* Selector de items por página y contador */}
      <div className="d-flex align-items-center gap-3 flex-wrap">
        {showPerPageSelector && (
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">Mostrar:</span>
            <Form.Select
              size={size}
              style={{ width: 'auto', minWidth: '70px' }}
              value={perPage}
              onChange={(e) => onPerPageChange(Number(e.target.value))}
              disabled={loading}
            >
              {perPageOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </div>
        )}
        
        {showResultsCount && (
          <span className="text-muted small">
            {total > 0 ? (
              <>
                Mostrando <strong>{startResult} - {endResult}</strong> de <strong>{total}</strong>
              </>
            ) : (
              'Sin resultados'
            )}
          </span>
        )}
        
        {/* Botón de refresh (opcional) */}
        {onRefresh && (
          <Button
            variant={pendingUpdates > 0 ? "warning" : "outline-secondary"}
            size={size}
            onClick={onRefresh}
            disabled={loading}
            className="d-flex align-items-center gap-1"
          >
            <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
            <span className="d-none d-sm-inline">Actualizar</span>
            {pendingUpdates > 0 && (
              <Badge bg="danger" pill>{pendingUpdates}</Badge>
            )}
          </Button>
        )}
      </div>
      
      {/* Controles de páginas */}
      {totalPages >= 1 && (
        <BSPagination size={size} className="mb-0">
          {/* Primera página */}
          <BSPagination.First
            disabled={page === 1 || loading}
            onClick={() => onPageChange(1)}
            aria-label="Primera página"
          />
          
          {/* Página anterior */}
          <BSPagination.Prev
            disabled={!hasPrevPage || loading}
            onClick={() => onPageChange(page - 1)}
            aria-label="Página anterior"
          />
          
          {/* Primera página + ellipsis */}
          {showFirstPage && (
            <>
              <BSPagination.Item
                onClick={() => onPageChange(1)}
                disabled={loading}
              >
                1
              </BSPagination.Item>
              {showFirstEllipsis && (
                <BSPagination.Ellipsis disabled />
              )}
            </>
          )}
          
          {/* Páginas visibles */}
          {visiblePages.map(pageNum => (
            <BSPagination.Item
              key={pageNum}
              active={pageNum === page}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              aria-label={`Página ${pageNum}`}
              aria-current={pageNum === page ? 'page' : undefined}
            >
              {pageNum}
            </BSPagination.Item>
          ))}
          
          {/* Última página + ellipsis */}
          {showLastPage && (
            <>
              {showLastEllipsis && (
                <BSPagination.Ellipsis disabled />
              )}
              <BSPagination.Item
                onClick={() => onPageChange(totalPages)}
                disabled={loading}
              >
                {totalPages}
              </BSPagination.Item>
            </>
          )}
          
          {/* Página siguiente */}
          <BSPagination.Next
            disabled={!hasNextPage || loading}
            onClick={() => onPageChange(page + 1)}
            aria-label="Página siguiente"
          />
          
          {/* Última página */}
          <BSPagination.Last
            disabled={page === totalPages || loading}
            onClick={() => onPageChange(totalPages)}
            aria-label="Última página"
          />
        </BSPagination>
      )}
    </div>
  );
};

PaginationControls.propTypes = {
  // Requeridos
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  hasPrevPage: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
  
  // Opcionales
  perPageOptions: PropTypes.arrayOf(PropTypes.number),
  showPerPageSelector: PropTypes.bool,
  showResultsCount: PropTypes.bool,
  maxVisiblePages: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'lg']),
  className: PropTypes.string,
  
  // Refresh
  onRefresh: PropTypes.func,
  loading: PropTypes.bool,
  pendingUpdates: PropTypes.number
};

export default PaginationControls;
